import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BooksForm from './BooksForm';
import Modal from './Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { downloadCardImage } from '../utils/downloadCard';
import { useLongPress } from '../hooks/useLongPress';
import { useAuth } from '../AuthContext';

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [originalBooks, setOriginalBooks] = useState([]);
  const [books, setBooks] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [comicFilter, setComicFilter] = useState('');
  const [universeFilter, setUniverseFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('original');

  const [showAddForm, setShowAddForm] = useState(false);

  const cardRefs = useRef([]);
  const downloadLongPress = useLongPress();
  const editLongPress = useLongPress();

  const [editingBook, setEditingBook] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPoster, setEditPoster] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const openEdit = (book) => {
    setEditingBook(book);
    setEditForm({
      title: book.title || '',
      author: book.author || '',
      rating: book.rating || '',
      thoughts: book.thoughts || '',
      comic: book.comic || false,
      marvel: book.marvel || false,
      dc: book.dc || false,
    });
    setEditPoster(null);
    setEditStatus('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.rating) { setEditStatus('Please fill in a title and rating.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let newPosterUrl = null;
      if (editPoster) newPosterUrl = await uploadPoster(editPoster);

      const patch = {
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        comic: editForm.comic,
        marvel: editForm.marvel,
        dc: editForm.dc,
        thoughts: editForm.thoughts || '',
      };
      if (editForm.author) patch.author = editForm.author;
      if (newPosterUrl) patch.poster_url = newPosterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/books?id=eq.${editingBook.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Update failed');

      const updated = {
        ...editingBook,
        title: editForm.title,
        author: editForm.author,
        rating: parseFloat(editForm.rating),
        thoughts: editForm.thoughts || '',
        comic: editForm.comic,
        marvel: editForm.marvel,
        dc: editForm.dc,
        ...(newPosterUrl ? { poster_url: newPosterUrl } : {}),
      };
      const updateList = (list) => list.map((b) => b.id === editingBook.id ? updated : b);
      setOriginalBooks(updateList);
      setBooks(updateList);

      setEditStatus('Saved :D');
      setTimeout(() => setEditingBook(null), 1000);
    } catch (err) {
      console.error(err);
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${editingBook.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/books?id=eq.${editingBook.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setOriginalBooks((prev) => prev.filter((b) => b.id !== editingBook.id));
      setBooks((prev) => prev.filter((b) => b.id !== editingBook.id));
      setEditingBook(null);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const fetchBooks = useCallback(async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/books?order=created_at.desc&select=*&limit=1000`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch books');
      const allBooks = await res.json();
      setOriginalBooks(allBooks);
      setBooks(allBooks);
    } catch (err) {
      console.error('Failed to fetch books:', err);
    }
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const applyFiltersAndSort = (opts = {}) => {
    const currentSearch = opts.searchTerm !== undefined ? opts.searchTerm : searchTerm;
    const currentRating = opts.ratingFilter !== undefined ? opts.ratingFilter : ratingFilter;
    const currentDate = opts.dateFilter !== undefined ? opts.dateFilter : dateFilter;
    const currentComic = opts.comicFilter !== undefined ? opts.comicFilter : comicFilter;
    const currentUniverse = opts.universeFilter !== undefined ? opts.universeFilter : universeFilter;
    const currentSort = opts.sortOrder !== undefined ? opts.sortOrder : sortOrder;

    let filtered = [...originalBooks];

    if (currentRating) {
      const min = parseFloat(currentRating);
      const max = min + 0.9;
      filtered = filtered.filter(
        (book) => book.rating >= min && book.rating <= max
      );
    }

    if (currentSearch) {
      filtered = filtered.filter((book) =>
        book.title.toLowerCase().includes(currentSearch.toLowerCase()) ||
        book.author?.toLowerCase().includes(currentSearch.toLowerCase())
      );
    }

    if (currentDate) {
      const now = new Date();
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - parseInt(currentDate));
      filtered = filtered.filter(
        (book) => new Date(book.created_at) >= pastDate
      );
    }

    if (currentComic === 'comics') {
      filtered = filtered.filter((book) => book.comic === true);
    } else if (currentComic === 'books') {
      filtered = filtered.filter((book) => book.comic !== true);
    }

    if (currentUniverse === 'marvel') {
      filtered = filtered.filter((book) => book.marvel === true);
    } else if (currentUniverse === 'dc') {
      filtered = filtered.filter((book) => book.dc === true);
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (currentSort === 'highest') {
      filtered.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    } else if (currentSort === 'lowest') {
      filtered.sort((a, b) => parseFloat(a.rating) - parseFloat(b.rating));
    }

    setBooks(filtered);
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    applyFiltersAndSort({ searchTerm: val });
  };

  const onRatingChange = (e) => {
    const val = e.target.value;
    setRatingFilter(val);
    applyFiltersAndSort({ ratingFilter: val });
  };

  const onDateChange = (e) => {
    const val = e.target.value;
    setDateFilter(val);
    applyFiltersAndSort({ dateFilter: val });
  };

  const onComicChange = (e) => {
    const val = e.target.value;
    setComicFilter(val);
    applyFiltersAndSort({ comicFilter: val });
  };

  const onUniverseChange = (e) => {
    const val = e.target.value;
    setUniverseFilter(val);
    applyFiltersAndSort({ universeFilter: val });
  };

  const onSortChange = (order) => {
    setSortOrder(order);
    applyFiltersAndSort({ sortOrder: order });
  };


  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Book Reviews</h1>
          <button onClick={() => navigate('/todo-books')} style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}>To-Do</button>
        </div>
        <div className="controls">
          {user && <button onClick={() => setShowAddForm(true)}>Add +</button>}
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={onSearchChange}
          />
          <button onClick={() => onSortChange('highest')} className={sortOrder === 'highest' ? 'btn-active' : ''}>Highest Rating</button>
          <button onClick={() => onSortChange('lowest')} className={sortOrder === 'lowest' ? 'btn-active' : ''}>Lowest Rating</button>
          <button onClick={() => onSortChange('original')} className={sortOrder === 'original' ? 'btn-active' : ''}>Newest</button>

          <select value={ratingFilter} onChange={onRatingChange}>
            <option value="">Filter by Rating</option>
            {[...Array(10)].map((_, i) => (
              <option key={i} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <select value={dateFilter} onChange={onDateChange}>
            <option value="">Filter by Date</option>
            <option value="7">Last Week</option>
            <option value="30">Last Month</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>

          <select value={comicFilter} onChange={onComicChange}>
            <option value="">Books/Comics</option>
            <option value="books">Books</option>
            <option value="comics">Comics</option>
          </select>

          <select value={universeFilter} onChange={onUniverseChange}>
            <option value="">Marvel/DC</option>
            <option value="marvel">Marvel</option>
            <option value="dc">DC</option>
          </select>
        </div>
      </div>

      <div className="movies-container">
        {books.map((book, idx) => (
          <div
            className="movie-card"
            key={idx}
            ref={(el) => (cardRefs.current[idx] = el)}
          >
            {book.poster_url && (
              <div className="movie-poster-wrap">
                <img
                  src={book.poster_url}
                  alt={`${book.title} Poster`}
                  className="movie-poster"
                  loading="lazy"
                  onDoubleClick={user ? () => downloadCardImage(cardRefs.current[idx], book.title) : undefined}
                  onTouchStart={user ? () => downloadLongPress.start(() => downloadCardImage(cardRefs.current[idx], book.title)) : undefined}
                  onTouchEnd={downloadLongPress.cancel}
                  onTouchCancel={downloadLongPress.cancel}
                  crossOrigin="anonymous"
                />
              </div>
            )}

            <div className="movie-content">
              <div className="movie-header">
                <h2>{book.title}</h2>
                <h3 className="book-author">{book.author}</h3>
                <span
                  className="movie-rating"
                  onDoubleClick={user ? () => openEdit(book) : undefined}
                  onTouchStart={user ? () => editLongPress.start(() => openEdit(book)) : undefined}
                  onTouchEnd={editLongPress.cancel}
                  onTouchCancel={editLongPress.cancel}
                >{Number(book.rating).toFixed(1)}</span>
              </div>
              <div className="movie-thoughts">
                {book.thoughts || '*'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {books.length === 0 && (
        <p className="no-results">Geen ene tering hier man.</p>
      )}

      {showAddForm && (
        <Modal onClose={() => setShowAddForm(false)}>
          <BooksForm onClose={() => { setShowAddForm(false); fetchBooks(); }} />
        </Modal>
      )}

      {editingBook && (
        <Modal onClose={() => setEditingBook(null)}>
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontFamily: 'angelWish', fontSize: '2.5rem', letterSpacing: '0.06em', margin: '0 0 1rem 0' }}>Edit Book</h2>
            <div className="form-group"><label>Title *</label><input type="text" name="title" value={editForm.title} onChange={handleEditChange} required /></div>
            <div className="form-group"><label>Author</label><input type="text" name="author" value={editForm.author} onChange={handleEditChange} /></div>
            <div className="form-group"><label>Rating *</label><input type="number" name="rating" value={editForm.rating} onChange={handleEditChange} min="1" max="10" step="0.1" required /></div>
            <div className="form-group"><label>Thoughts</label><textarea name="thoughts" value={editForm.thoughts} onChange={handleEditChange} rows={4} /></div>
            <div className="form-group">
              <label>Poster</label>
              {editingBook?.poster_url && !editPoster && (
                <img src={editingBook.poster_url} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={(e) => setEditPoster(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label>Comic/Book</label>
              <div className="form-checkboxes">
                <label><input type="radio" name="comic" checked={!editForm.comic} onChange={() => setEditForm(prev => ({ ...prev, comic: false }))} /> Book</label>
                <label><input type="radio" name="comic" checked={editForm.comic} onChange={() => setEditForm(prev => ({ ...prev, comic: true }))} /> Comic</label>
              </div>
            </div>
            <div className="form-group">
              <label>Marvel/DC</label>
              <div className="form-checkboxes">
                <label><input type="checkbox" name="marvel" checked={editForm.marvel} onChange={handleEditChange} /> Marvel</label>
                <label><input type="checkbox" name="dc" checked={editForm.dc} onChange={handleEditChange} /> DC</label>
              </div>
            </div>
            <div className="edit-modal-actions">
              <button type="button" className="form-submit" style={{ background: '#eee', color: '#333', borderColor: '#999', WebkitTextFillColor: '#333' }} onClick={() => setEditingBook(null)}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}

export default App;
